// Blog articles. Titles, descriptions, dates and reading times live in meta.json (the
// server reads it too, for search engines and link previews); the article text is in
// bodies.js, loaded only when a blog page needs it.
import META from './meta.json';
import { reloadForUpdate } from '../errorReport';

// Blog categories, in the order of the filter buttons.
export const CATEGORIES = [
  { id: 'prop', label: 'Prop firm rules' },
  { id: 'risk', label: 'Risk management' },
  { id: 'strategy', label: 'Trading strategies' },
  { id: 'start', label: 'Getting started' },
];

// An article goes live on its date, at midnight New York time, so a batch can be published
// ahead of time and appear day by day. The server uses the same rule (prerender.js, server.js).
export function todayKey(now = new Date()) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

// Every article, newest first, including ones that aren't live yet.
export const ALL_POSTS = META.map((m) => ({ ...m })).sort((a, b) => b.date.localeCompare(a.date));

// The articles that are live today (worked out again when the date changes).
let live = { day: null, list: [] };
export function livePosts(now = new Date()) {
  const day = todayKey(now);
  if (live.day !== day) live = { day, list: ALL_POSTS.filter((p) => p.date <= day) };
  return live.list;
}

// Newest first, with the text in each language: the live articles when the page loaded.
export const POSTS = livePosts();

// The article text, fetched once and shared by every blog page.
let bodies = null;
export function loadBodies() {
  if (!bodies) {
    bodies = import('./bodies').then((m) => m.default).catch((err) => {
      bodies = null;
      reloadForUpdate(err);
      throw err;
    });
  }
  return bodies;
}

// Hand-picked "Keep reading" articles: the topics each article builds on or leads to.
const RELATED = {
  'fomc-meeting-explained': ['fed-and-interest-rates', 'news-trading-prop-firm-rules', 'pce-inflation-explained'],
  'pce-inflation-explained': ['inflation-and-cpi-explained', 'gdp-report-explained', 'fomc-meeting-explained'],
  'gdp-report-explained': ['what-moves-the-markets', 'ppi-explained', 'pce-inflation-explained'],
  'ppi-explained': ['pce-inflation-explained', 'inflation-and-cpi-explained', 'retail-sales-report-explained'],
  'retail-sales-report-explained': ['gdp-report-explained', 'jolts-report-explained', 'ppi-explained'],
  'jolts-report-explained': ['jobs-report-nfp-explained', 'ism-pmi-explained', 'retail-sales-report-explained'],
  'ism-pmi-explained': ['what-moves-the-markets', 'jobless-claims-explained', 'jolts-report-explained'],
  'jobless-claims-explained': ['jobs-report-nfp-explained', 'jolts-report-explained', 'eia-crude-oil-inventories-explained'],
  'eia-crude-oil-inventories-explained': ['jobless-claims-explained'],
  'stop-buying-prop-firm-accounts': ['trading-is-a-business', 'spot-prop-evaluation-failure-in-your-trades', 'after-blowing-a-prop-account'],
  'sp-500-explained': ['magnificent-7-stocks', 'es-vs-nq-vs-micros', 'futures-market-hours'],
  'find-your-edge-in-your-journal': ['how-traders-make-money-edge-expectancy', 'write-a-playbook-play', 'find-your-biggest-trading-leak'],
  'what-is-a-stock': ['bid-ask-spread-slippage', 'sp-500-explained', 'how-the-stock-market-works'],
  'who-do-you-want-to-become-trader': ['find-your-biggest-trading-leak', 'turn-a-bad-habit-into-a-rule', 'trading-habits-build-and-break'],
  'real-cost-of-prop-trading': ['keep-your-trading-costs-low', 'stop-buying-prop-firm-accounts', 'choose-prop-firm-account-size'],
  'nasdaq-100-explained': ['magnificent-7-stocks', 'start-with-micro-futures', 'bonds-and-yields-explained'],
  'r-multiple-explained': ['stop-moving-your-stop-loss', 'good-win-rate-profit-factor', 'trade-management-after-entry'],
  'how-the-stock-market-works': ['order-types-explained', 'pdt-rule-cash-vs-margin', 'ticks-points-margin-leverage'],
  'trading-habits-build-and-break': ['trading-plan-and-routine', 'set-your-own-daily-loss-limit', 'journaling-streak'],
  'track-trading-expenses': ['prop-firm-payout-taxes', 'real-cost-of-prop-trading', 'trading-is-a-business'],
  'dow-jones-explained': ['ticks-points-margin-leverage', 'how-the-indexes-move-together'],
  'trade-management-after-entry': ['write-a-playbook-play', 'stop-moving-your-stop-loss', 'cutting-winners-too-early'],
  'day-trading-stocks': ['how-the-stock-market-works', 'pdt-rule-cash-vs-margin', 'stocks-in-play'],
  'know-yourself-as-a-trader': ['risk-management-basics', 'set-your-own-daily-loss-limit', 'trading-psychology-basics'],
  'build-a-trading-playbook': ['how-to-tag-trades', 'write-a-playbook-play', 'pullback-trading-strategy'],
  'russell-2000-explained': ['fed-and-interest-rates', 'bid-ask-spread-slippage', 'risk-on-risk-off-explained'],
  'economic-calendar-for-traders': ['inflation-and-cpi-explained', 'jobs-report-nfp-explained', 'fed-and-interest-rates'],
  'stocks-in-play': ['float-volume-relative-volume', 'how-the-indexes-move-together', 'how-the-stock-market-works'],
  'trading-accountability-partner': ['five-numbers-to-check-every-week', 'grade-your-trading-week', 'who-do-you-want-to-become-trader'],
  'write-a-playbook-play': ['what-is-vwap', 'turn-a-bad-habit-into-a-rule', 'build-a-trading-playbook'],
  'magnificent-7-stocks': ['sp-500-explained', 'nasdaq-100-explained', 'earnings-season-and-the-indexes'],
  'quarterly-trading-review': ['real-cost-of-prop-trading', 'find-your-edge-in-your-journal', 'find-your-biggest-trading-leak'],
  'float-volume-relative-volume': ['bid-ask-spread-slippage', 'stocks-in-play', 'build-a-trading-playbook'],
  'should-you-trade-more-than-futures': ['markets-you-can-trade', 'what-is-a-stock', 'revenge-trading'],
  'how-the-indexes-move-together': ['risk-on-risk-off-explained', 'vix-fear-gauge-explained', 'sp-500-explained'],
  'journaling-streak': ['how-to-tag-trades', 'trading-tired-sleep-and-stress', 'grade-your-trading-month'],
  'trading-earnings-day-trader': ['earnings-season-and-the-indexes', 'magnificent-7-stocks', 'pdt-rule-cash-vs-margin'],
  'pdt-rule-cash-vs-margin': ['how-much-money-to-start-prop-trading', 'stocks-vs-futures-day-trading'],
  'stocks-vs-futures-day-trading': ['stocks-in-play', 'how-prop-firm-payouts-work', 'best-time-to-trade-futures'],
  'what-moves-the-markets': ['fed-and-interest-rates', 'news-trading-prop-firm-rules', 'risk-on-risk-off-explained'],
  'fed-and-interest-rates': ['inflation-and-cpi-explained', 'bonds-and-yields-explained', 'news-trading-prop-firm-rules'],
  'inflation-and-cpi-explained': ['fed-and-interest-rates', 'jobs-report-nfp-explained', 'bid-ask-spread-slippage'],
  'jobs-report-nfp-explained': ['inflation-and-cpi-explained', 'opening-range-breakout', 'set-your-own-daily-loss-limit'],
  'stock-indexes-explained': ['es-vs-nq-vs-micros', 'earnings-season-and-the-indexes', 'start-with-micro-futures'],
  'bonds-and-yields-explained': ['fed-and-interest-rates', 'stock-indexes-explained', 'risk-on-risk-off-explained'],
  'dollar-oil-and-gold': ['bonds-and-yields-explained', 'find-your-biggest-trading-leak', 'ticks-points-margin-leverage'],
  'earnings-season-and-the-indexes': ['stock-indexes-explained', 'what-moves-the-markets', 'vix-fear-gauge-explained'],
  'vix-fear-gauge-explained': ['risk-on-risk-off-explained', 'position-size-stop-loss-take-profit', 'earnings-season-and-the-indexes'],
  'risk-on-risk-off-explained': ['what-moves-the-markets', 'vix-fear-gauge-explained', 'trading-plan-and-routine'],
  'stop-moving-your-stop-loss': ['position-size-stop-loss-take-profit', 'averaging-down-adding-to-losers', 'process-vs-outcome-trading'],
  'lunch-hour-trading-trap': ['best-time-to-trade-futures', 'find-your-biggest-trading-leak', 'how-many-trades-per-day'],
  'overconfidence-after-a-win': ['position-size-creep', 'revenge-trading', 'how-many-trades-per-day'],
  'position-size-creep': ['position-size-stop-loss-take-profit', 'overconfidence-after-a-win', 'start-with-micro-futures'],
  'how-many-trades-per-day': ['overtrading-how-to-stop', 'lunch-hour-trading-trap', 'keep-your-trading-costs-low'],
  'switching-accounts-after-losses': ['multiple-prop-accounts-copy-trading', 'revenge-trading', 'set-your-own-daily-loss-limit'],
  'trading-tired-sleep-and-stress': ['trading-psychology-basics', 'what-is-tilt-how-to-measure-it', 'trading-plan-and-routine'],
  'averaging-down-adding-to-losers': ['stop-moving-your-stop-loss', 'long-short-position-terms', 'position-size-creep'],
  'cutting-winners-too-early': ['how-traders-make-money-edge-expectancy', 'good-win-rate-profit-factor', 'position-size-stop-loss-take-profit'],
  'turn-a-bad-habit-into-a-rule': ['find-your-biggest-trading-leak', 'grade-your-trading-week', 'trading-plan-and-routine'],
  'grade-your-trading-week': ['weekly-trading-review-template', 'process-vs-outcome-trading', 'five-numbers-to-check-every-week'],
  'grade-your-trading-month': ['grade-your-trading-week', 'find-your-biggest-trading-leak', 'am-i-improving-as-a-trader'],
  'process-vs-outcome-trading': ['grade-your-trading-week', 'how-to-tag-trades', 'revenge-trading'],
  'what-is-tilt-how-to-measure-it': ['revenge-trading', 'set-your-own-daily-loss-limit', 'trading-psychology-basics'],
  'five-numbers-to-check-every-week': ['good-win-rate-profit-factor', 'trading-performance-terms', 'grade-your-trading-week'],
  'spot-prop-evaluation-failure-in-your-trades': ['why-traders-fail-evaluations-first-week', 'trailing-drawdown-explained', 'consistency-rule-explained'],
  'find-your-biggest-trading-leak': ['best-time-to-trade-futures', 'what-is-tilt-how-to-measure-it', 'how-to-tag-trades'],
  'am-i-improving-as-a-trader': ['five-numbers-to-check-every-week', 'grade-your-trading-month', 'after-blowing-a-prop-account'],
  'order-types-explained': ['bid-ask-spread-slippage', 'position-size-stop-loss-take-profit', 'trailing-drawdown-explained'],
  'bid-ask-spread-slippage': ['order-types-explained', 'futures-market-hours', 'keep-your-trading-costs-low'],
  'ticks-points-margin-leverage': ['es-vs-nq-vs-micros', 'start-with-micro-futures', 'position-size-stop-loss-take-profit'],
  'long-short-position-terms': ['ticks-points-margin-leverage', 'market-structure-trend-vs-range', 'how-to-tag-trades'],
  'prop-firm-terms-glossary': ['trailing-drawdown-explained', 'consistency-rule-explained', 'how-prop-firm-payouts-work'],
  'trading-performance-terms': ['good-win-rate-profit-factor', 'how-traders-make-money-edge-expectancy', 'weekly-trading-review-template'],
  'fomo-in-trading': ['revenge-trading', 'pullback-trading-strategy', 'how-to-tag-trades'],
  'pullback-trading-strategy': ['market-structure-trend-vs-range', 'what-is-vwap', 'fomo-in-trading'],
  'choose-prop-firm-account-size': ['how-much-money-to-start-prop-trading', 'trailing-drawdown-explained', 'prop-firm-scaling-plans-contract-limits'],
  'paper-trading-vs-live': ['start-with-micro-futures', 'how-to-pass-a-prop-firm-challenge', 'how-to-tag-trades'],
  'how-to-tag-trades': ['how-to-keep-a-trading-journal', 'weekly-trading-review-template', 'fomo-in-trading'],
  'trading-is-a-business': ['how-much-money-to-start-prop-trading', 'keep-your-trading-costs-low', 'prop-firm-payout-taxes'],
  'weekly-trading-review-template': ['good-win-rate-profit-factor', 'trading-plan-and-routine', 'how-to-keep-a-trading-journal'],
  'good-win-rate-profit-factor': ['how-traders-make-money-edge-expectancy', 'prop-firm-evaluation-full-time-job', 'keep-your-trading-costs-low'],
  'prop-firm-evaluation-full-time-job': ['overtrading-how-to-stop', 'futures-market-hours', 'how-long-to-pass-prop-firm-evaluation'],
  'overtrading-how-to-stop': ['after-blowing-a-prop-account', 'revenge-trading', 'set-your-own-daily-loss-limit'],
  'after-blowing-a-prop-account': ['weekly-trading-review-template', 'what-happens-when-you-break-a-prop-firm-rule', 'how-much-money-to-start-prop-trading'],
  'what-is-trading': ['markets-you-can-trade', 'how-to-keep-a-trading-journal', 'how-prop-firms-got-into-futures'],
  'markets-you-can-trade': ['trading-styles-explained', 'what-is-a-futures-contract', 'start-with-micro-futures'],
  'trading-styles-explained': ['how-traders-make-money-edge-expectancy', 'futures-market-hours', 'best-time-to-trade-futures'],
  'how-traders-make-money-edge-expectancy': ['risk-management-basics', 'position-size-stop-loss-take-profit', 'keep-your-trading-costs-low'],
  'risk-management-basics': ['trading-psychology-basics', 'set-your-own-daily-loss-limit', 'trailing-drawdown-explained'],
  'trading-psychology-basics': ['trading-plan-and-routine', 'revenge-trading', 'set-your-own-daily-loss-limit'],
  'trading-plan-and-routine': ['what-is-a-futures-contract', 'how-to-keep-a-trading-journal', 'market-structure-trend-vs-range'],
  'what-is-a-futures-contract': ['history-of-futures-trading', 'start-with-micro-futures', 'es-vs-nq-vs-micros'],
  'history-of-futures-trading': ['who-trades-futures-and-why', 'futures-trading-floor-open-outcry', 'how-prop-firms-got-into-futures'],
  'who-trades-futures-and-why': ['how-futures-contracts-work', 'futures-market-hours', 'how-to-keep-a-trading-journal'],
  'how-futures-contracts-work': ['futures-market-hours', 'position-size-stop-loss-take-profit', 'es-vs-nq-vs-micros'],
  'futures-market-hours': ['futures-trading-floor-open-outcry', 'best-time-to-trade-futures', 'news-trading-prop-firm-rules'],
  'futures-trading-floor-open-outcry': ['how-a-futures-trade-gets-filled', 'history-of-futures-trading', 'volume-profile-basics'],
  'how-a-futures-trade-gets-filled': ['how-prop-firms-got-into-futures', 'keep-your-trading-costs-low', 'are-prop-firms-legit'],
  'how-prop-firms-got-into-futures': ['is-a-funded-account-real-money', 'how-to-pass-a-prop-firm-challenge', 'are-prop-firms-legit'],
  'market-structure-trend-vs-range': ['support-and-resistance-levels', 'how-to-keep-a-trading-journal', 'trailing-drawdown-explained'],
  'support-and-resistance-levels': ['what-is-vwap', 'position-size-stop-loss-take-profit', 'market-structure-trend-vs-range'],
  'what-is-vwap': ['anchored-vwap-explained', 'support-and-resistance-levels', 'position-size-stop-loss-take-profit'],
  'opening-range-breakout': ['volume-profile-basics', 'best-time-to-trade-futures', 'es-vs-nq-vs-micros'],
  'volume-profile-basics': ['market-structure-trend-vs-range', 'what-is-vwap', 'how-to-keep-a-trading-journal'],
  'is-a-funded-account-real-money': ['how-prop-firm-payouts-work', 'are-prop-firms-legit', 'prop-firm-payout-taxes'],
  'are-prop-firms-legit': ['is-a-funded-account-real-money', 'topstep-vs-apex-vs-myfundedfutures', 'how-much-money-to-start-prop-trading'],
  'prop-firm-scaling-plans-contract-limits': ['es-vs-nq-vs-micros', 'position-size-stop-loss-take-profit', 'multiple-prop-accounts-copy-trading'],
  'prop-firm-payout-taxes': ['how-prop-firm-payouts-work', 'is-a-funded-account-real-money', 'how-much-money-to-start-prop-trading'],
  'es-vs-nq-vs-micros': ['start-with-micro-futures', 'position-size-stop-loss-take-profit', 'prop-firm-scaling-plans-contract-limits'],
  'how-to-keep-a-trading-journal': ['revenge-trading', 'best-time-to-trade-futures', 'set-your-own-daily-loss-limit'],
  'revenge-trading': ['set-your-own-daily-loss-limit', 'what-happens-when-you-break-a-prop-firm-rule', 'how-to-keep-a-trading-journal'],
  'best-time-to-trade-futures': ['news-trading-prop-firm-rules', 'how-to-keep-a-trading-journal', 'position-size-stop-loss-take-profit'],
  'how-long-to-pass-prop-firm-evaluation': ['consistency-rule-explained', 'how-to-pass-a-prop-firm-challenge', 'why-traders-fail-evaluations-first-week'],
  'set-your-own-daily-loss-limit': ['revenge-trading', 'position-size-stop-loss-take-profit', 'what-happens-when-you-break-a-prop-firm-rule'],
  'multiple-prop-accounts-copy-trading': ['how-much-money-to-start-prop-trading', 'keep-your-trading-costs-low', 'how-to-pass-a-prop-firm-challenge'],
  'news-trading-prop-firm-rules': ['best-time-to-trade-futures', 'what-happens-when-you-break-a-prop-firm-rule', 'position-size-stop-loss-take-profit'],
  'how-much-money-to-start-prop-trading': ['multiple-prop-accounts-copy-trading', 'keep-your-trading-costs-low', 'how-to-pass-a-prop-firm-challenge'],
  'what-happens-when-you-break-a-prop-firm-rule': ['trailing-drawdown-explained', 'consistency-rule-explained', 'news-trading-prop-firm-rules'],
  'how-to-pass-a-prop-firm-challenge': ['trailing-drawdown-explained', 'position-size-stop-loss-take-profit', 'why-traders-fail-evaluations-first-week'],
  'how-prop-firm-payouts-work': ['is-a-funded-account-real-money', 'prop-firm-payout-taxes', 'consistency-rule-explained'],
  'why-traders-fail-evaluations-first-week': ['how-to-pass-a-prop-firm-challenge', 'position-size-stop-loss-take-profit', 'trailing-drawdown-explained'],
  'trailing-drawdown-explained': ['what-happens-when-you-break-a-prop-firm-rule', 'why-traders-fail-evaluations-first-week', 'position-size-stop-loss-take-profit'],
  'consistency-rule-explained': ['how-long-to-pass-prop-firm-evaluation', 'how-prop-firm-payouts-work', 'trailing-drawdown-explained'],
  'position-size-stop-loss-take-profit': ['set-your-own-daily-loss-limit', 'trailing-drawdown-explained', 'start-with-micro-futures'],
  'keep-your-trading-costs-low': ['how-much-money-to-start-prop-trading', 'start-with-micro-futures', 'topstep-vs-apex-vs-myfundedfutures'],
  'anchored-vwap-explained': ['opening-range-breakout', 'what-is-vwap', 'position-size-stop-loss-take-profit'],
  'topstep-vs-apex-vs-myfundedfutures': ['are-prop-firms-legit', 'how-to-pass-a-prop-firm-challenge', 'how-prop-firm-payouts-work'],
  'start-with-micro-futures': ['es-vs-nq-vs-micros', 'why-traders-fail-evaluations-first-week', 'position-size-stop-loss-take-profit'],
};

// Three articles to read next: the hand-picked ones, then others from the same category, then the newest.
export function relatedPosts(slug, count = 3) {
  const post = findPost(slug);
  const picked = (RELATED[slug] || []).map(findPost).filter(Boolean);
  const rest = livePosts().filter((p) => p.slug !== slug && !picked.includes(p))
    .sort((a, b) => (b.category === post?.category) - (a.category === post?.category));
  return [...picked, ...rest].slice(0, count);
}

// The "Prop firm essentials" step at the end of the /learn page.
export const ESSENTIALS = ['how-to-pass-a-prop-firm-challenge', 'trailing-drawdown-explained', 'consistency-rule-explained', 'how-prop-firm-payouts-work', 'what-happens-when-you-break-a-prop-firm-rule'];

// Articles meant to be read in order. Each one's meta.json entry names its series and part.
// They're listed in learning order on the /learn page.
export const SERIES = {
  'journey-profitable': {
    en: 'Am I profitable?',
    es: '¿Soy rentable?',
    blurb: {
      en: 'What prop firms really cost, what they paid you back, and whether your trading is making money once every fee is counted.',
      es: 'Lo que de verdad cuestan las firmas de fondeo, lo que te han regresado y si tu trading deja dinero cuando cuentas cada cuota.',
    },
  },
  'journey-better': {
    en: 'Am I getting better?',
    es: '¿Estoy mejorando?',
    blurb: {
      en: 'How to measure real progress over months and years: then vs now, how long your accounts last, pass rate and the changes that worked.',
      es: 'Cómo medir el avance real en meses y años: antes y ahora, cuánto duran tus cuentas, la tasa de aprobación y los cambios que funcionaron.',
    },
  },
  'journey-edge': {
    en: 'My edge & milestones',
    es: 'Mi ventaja y logros',
    blurb: {
      en: 'Find what works over your whole career, what keeps costing you, and the milestones from your first challenge to your first payout.',
      es: 'Encuentra lo que funciona en toda tu carrera, lo que te sigue costando y los logros desde tu primera evaluación hasta tu primer retiro.',
    },
  },
  'intro-trading': {
    en: 'Intro to trading',
    es: 'Introducción al trading',
    blurb: {
      en: 'What trading is, the markets, trading styles, how traders make money, risk, psychology and your plan.',
      es: 'Qué es el trading, los mercados, los estilos, cómo ganan dinero los traders, el riesgo, la psicología y tu plan.',
    },
  },
  'intro-futures': {
    en: 'Intro to futures trading',
    es: 'Introducción al trading de futuros',
    blurb: {
      en: 'What a futures contract is, its history, who trades it, how contracts and hours work, and how prop firms got here.',
      es: 'Qué es un contrato de futuros, su historia, quién lo opera, cómo funcionan los contratos y los horarios, y cómo llegaron las empresas de fondeo.',
    },
  },
  'trading-terms': {
    en: 'Trading terms explained',
    es: 'Términos de trading explicados',
    blurb: {
      en: 'Order types, bid and ask, ticks and leverage, the words for your position, prop firm terms and the numbers in your journal.',
      es: 'Tipos de orden, bid y ask, ticks y apalancamiento, las palabras de tu posición, los términos de fondeo y los números de tu diario.',
    },
  },
  'technical-analysis': {
    en: 'Technical analysis for prop traders',
    es: 'Análisis técnico para traders de cuentas fondeadas',
    blurb: {
      en: 'Market structure, support and resistance, VWAP, anchored VWAP, the opening range and volume profile.',
      es: 'Estructura del mercado, soportes y resistencias, VWAP, VWAP anclado, el rango de apertura y el perfil de volumen.',
    },
  },
  'getting-better': {
    en: 'Getting better as a trader',
    es: 'Mejora como trader',
    blurb: {
      en: 'The weekly review, what good numbers look like, trading around a job, overtrading and coming back from a blown account.',
      es: 'La revisión semanal, cómo se ven los buenos números, operar con un trabajo, operar de más y volver después de quemar una cuenta.',
    },
  },
  'grade-your-trading': {
    en: 'Grade your trading',
    es: 'Califica tu trading',
    blurb: {
      en: 'Grade your week and your month, process vs. outcome, tilt, the five weekly numbers, prop evaluations, your biggest leak and tracking progress.',
      es: 'Califica tu semana y tu mes, proceso contra resultado, el tilt, los cinco números de la semana, las evaluaciones de fondeo, tu mayor fuga y cómo seguir tu avance.',
    },
  },
  'financial-markets': {
    en: 'How the financial markets work',
    es: 'Cómo funcionan los mercados financieros',
    blurb: {
      en: 'What moves the markets: the Fed, inflation, jobs, stock indexes, bonds, the dollar, oil and gold, earnings, the VIX and risk-on vs. risk-off.',
      es: 'Qué mueve los mercados: la Fed, la inflación, el empleo, los índices, los bonos, el dólar, el petróleo y el oro, los reportes, el VIX y el apetito por el riesgo.',
    },
  },
  'fix-the-habit': {
    en: 'Fix the habit',
    es: 'Corrige el hábito',
    blurb: {
      en: 'The habits that quietly cost traders money, how to spot each one in your own trades, and the rule that fixes it.',
      es: 'Los hábitos que le cuestan dinero a los traders sin que lo noten, cómo ver cada uno en tus operaciones y la regla que lo corrige.',
    },
  },
  'trading-business': {
    en: "Run your trading like a business",
    es: "Maneja tu trading como un negocio",
    blurb: {
      en: "Why buying more prop accounts isn’t a plan, what prop trading really costs, tracking expenses, building a playbook and whether to trade more than futures.",
      es: "Por qué comprar más cuentas de fondeo no es un plan, cuánto cuesta de verdad, cómo llevar tus gastos, cómo armar tus estrategias y si conviene operar más que futuros.",
    },
  },
  'index-deep-dive': {
    en: "The stock indexes up close",
    es: "Los índices bursátiles a fondo",
    blurb: {
      en: "The S&P 500, the Nasdaq-100, the Dow, the Russell 2000 and the Magnificent 7: what each one is, how it’s built, its futures, and how they move together.",
      es: "El S&P 500, el Nasdaq-100, el Dow, el Russell 2000 y los 7 Magníficos: qué es cada uno, cómo se arma, sus futuros y cómo se mueven juntos.",
    },
  },
  'find-your-edge': {
    en: "Find and grow your edge",
    es: "Encuentra y haz crecer tu ventaja",
    blurb: {
      en: "Find your edge in your own trades, measure in R, manage trades after entry, use the economic calendar, review every quarter and journal every day.",
      es: "Encuentra tu ventaja en tus propias operaciones, mide en R, maneja la operación después de entrar, usa el calendario económico, revisa cada trimestre y escribe tu diario todos los días.",
    },
  },
  'intro-stocks': {
    en: "Intro to stocks",
    es: "Introducción a las acciones",
    blurb: {
      en: "What a stock is, how the stock market works, day trading stocks, stocks in play, float and volume, earnings, the PDT rule, and stocks vs. futures.",
      es: "Qué es una acción, cómo funciona la bolsa, el day trading de acciones, las acciones en juego, el float y el volumen, los reportes, la regla PDT, y acciones vs. futuros.",
    },
  },
  'become-the-trader': {
    en: "Become the trader you want to be",
    es: "Conviértete en el trader que quieres ser",
    blurb: {
      en: "Picture the trader you want to become, build and break habits, know your strengths, weaknesses and risk tolerance, and find someone to keep you accountable.",
      es: "Imagina al trader que quieres ser, construye y rompe hábitos, conoce tus fortalezas, debilidades y tolerancia al riesgo, y encuentra a alguien que te haga rendir cuentas.",
    },
  },
  'economic-reports': {
    en: "Economic reports explained",
    es: "Los reportes económicos explicados",
    blurb: {
      en: "Every report on the Market events calendar, from zero: what it stands for, who publishes it, when it comes out, what it measures and why futures move on it.",
      es: "Cada reporte del calendario de Eventos de mercado, desde cero: qué significa, quién lo publica, cuándo sale, qué mide y por qué mueve los futuros.",
    },
  },
};

// The articles of one series, in order.
export function seriesParts(id) {
  return livePosts().filter((p) => p.series === id).sort((a, b) => a.seriesPart - b.seriesPart);
}

export function seriesOf(slug) {
  const post = findPost(slug);
  if (!post || !SERIES[post.series]) return null;
  const parts = seriesParts(post.series);
  return { id: post.series, title: SERIES[post.series], parts, index: parts.indexOf(post) };
}

export function findPost(slug) {
  return livePosts().find((p) => p.slug === slug) || null;
}

// Rough reading time in minutes.
export function readingMinutes(blocks) {
  const words = blocks.reduce((n, b) => {
    const text = [b.p, b.h2, b.callout, b.note, b.label, b.caption, ...(b.list || []), ...(b.table ? b.table.rows.flat() : [])].filter(Boolean).join(' ');
    return n + text.split(/\s+/).length;
  }, 0);
  return Math.max(1, Math.round(words / 230));
}
